import { useState } from "react";
import { Table, Button, Badge, Form } from "react-bootstrap";
import {
  FaEdit,
  FaTrash,
  FaCalendarAlt,
  FaSave,
  FaClock,
} from "react-icons/fa";

function StockCallsTable({
  calls,
  editCall,
  deleteCall,
  quickUpdateCall,
  search,
  typeFilter,
  statusFilter,
}) {
  const [quickUpdates, setQuickUpdates] = useState({});

  const filteredCalls = calls.filter((call) => {
    const matchesSearch = call.stockName
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchesType =
      typeFilter === "All" || call.callType === typeFilter;

    const matchesStatus =
      statusFilter === "All" || call.status === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "Active":
        return <Badge bg="success">🟢 Active</Badge>;

      case "Target Hit":
        return <Badge bg="primary">🎯 Target Hit</Badge>;

      case "Stop Loss Hit":
        return <Badge bg="danger">🔴 Stop Loss Hit</Badge>;

      case "Exit":
        return (
          <Badge bg="warning" text="dark">
            🚪 Exit
          </Badge>
        );

      case "Closed":
        return <Badge bg="secondary">⚫ Closed</Badge>;

      default:
        return <Badge bg="secondary">-</Badge>;
    }
  };

  const getCallTypeBadge = (type) => {
    switch (type) {
      case "Long Term":
        return <Badge bg="success">Long Term</Badge>;

      case "Mid Term":
        return (
          <Badge bg="warning" text="dark">
            Mid Term
          </Badge>
        );

      case "Short Term":
        return <Badge bg="danger">Short Term</Badge>;

      default:
        return <Badge bg="secondary">{type}</Badge>;
    }
  };

  const handleQuickChange = (callId, field, value) => {
    setQuickUpdates((previous) => ({
      ...previous,
      [callId]: {
        ...previous[callId],
        [field]: value,
      },
    }));
  };

  const getQuickValue = (call, field) => {
    if (quickUpdates[call.id]?.[field] !== undefined) {
      return quickUpdates[call.id][field];
    }

    return call[field] || "";
  };

  return (
    <div className="shadow rounded bg-white p-3">
      <h4 className="mb-4">📋 Recent Stock Calls</h4>

      <Table hover responsive bordered>
        <thead className="table-dark">
          <tr>
            <th>Stock</th>
            <th>Type</th>
            <th>Status</th>
            <th>Date & Time</th>
            <th>Buy</th>
            <th>Calc Price</th>
            <th>CMP</th>
            <th>P/L %</th>
            <th>P/L ₹</th>
            <th>Target</th>
            <th>SL</th>
            <th>Quick Update</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {filteredCalls.length === 0 ? (
            <tr>
              <td colSpan="13" className="text-center">
                No Stock Calls Found
              </td>
            </tr>
          ) : (
            filteredCalls.map((call) => {
              const buyText = call.buyPrice || "-";
              const targetText = call.targetPrice || "-";
              const stopLossText = call.stopLoss || "-";

              const entry = Number(
                call.entryPrice || parseFloat(call.buyPrice) || 0
              );

              const cmp = Number(call.currentPrice || entry);

              const profitAmount = cmp - entry;

              const profitPercent =
                entry > 0
                  ? ((profitAmount / entry) * 100).toFixed(2)
                  : "0.00";

              const quickCMP = getQuickValue(call, "currentPrice");
              const quickStatus = getQuickValue(call, "status");

              return (
                <tr key={call.id}>
                  <td>
                    <strong>{call.stockName}</strong>
                  </td>

                  <td>{getCallTypeBadge(call.callType)}</td>

                  <td>{getStatusBadge(call.status)}</td>

                  <td>
                    <div>
                      <FaCalendarAlt className="me-1 text-primary" />
                      {call.recommendationDate || "-"}
                    </div>

                    <div className="text-muted small mt-1">
                      <FaClock className="me-1" />
                      {call.recommendationTime || "-"}
                    </div>
                  </td>

                  <td>
                    <strong>₹ {buyText}</strong>
                  </td>

                  <td>₹ {entry}</td>

                  <td>
                    <strong>₹ {cmp}</strong>
                  </td>

                  <td
                    className={
                      profitAmount >= 0
                        ? "text-success fw-bold"
                        : "text-danger fw-bold"
                    }
                  >
                    {profitAmount >= 0 ? "+" : ""}
                    {profitPercent}%
                  </td>

                  <td
                    className={
                      profitAmount >= 0
                        ? "text-success fw-bold"
                        : "text-danger fw-bold"
                    }
                  >
                    {profitAmount >= 0 ? "+" : ""}
                    ₹{profitAmount.toFixed(2)}
                  </td>

                  <td>₹ {targetText}</td>

                  <td>₹ {stopLossText}</td>

                  <td style={{ minWidth: "220px" }}>
                    <Form.Control
                      type="number"
                      size="sm"
                      className="mb-2"
                      value={quickCMP}
                      onChange={(e) =>
                        handleQuickChange(
                          call.id,
                          "currentPrice",
                          e.target.value
                        )
                      }
                      placeholder="CMP"
                    />

                    <Form.Select
                      size="sm"
                      className="mb-2"
                      value={quickStatus}
                      onChange={(e) =>
                        handleQuickChange(
                          call.id,
                          "status",
                          e.target.value
                        )
                      }
                    >
                      <option>Active</option>
                      <option>Target Hit</option>
                      <option>Stop Loss Hit</option>
                      <option>Exit</option>
                      <option>Closed</option>
                    </Form.Select>

                    <Button
                      variant="success"
                      size="sm"
                      className="w-100"
                      onClick={() =>
                        quickUpdateCall(
                          call.id,
                          quickCMP,
                          quickStatus
                        )
                      }
                    >
                      <FaSave className="me-1" />
                      Update
                    </Button>
                  </td>

                  <td>
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-2 mb-2"
                      onClick={() => editCall(call)}
                    >
                      <FaEdit />
                    </Button>

                    <Button
                      variant="danger"
                      size="sm"
                      className="mb-2"
                      onClick={() => deleteCall(call.id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </div>
  );
}

export default StockCallsTable;